import React, { useState } from "react";
import axios from "axios";
import { useSiteContent } from "../i18n/SiteContent";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { CaretDown, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FaqItem({ idx, q, a, open, onToggle }) {
  return (
    <li data-testid={`faq-row-${idx}`}
      className={`group transition-colors ${open ? "bg-slate-50" : ""}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-start flex items-start gap-6 px-6 md:px-8 py-6 md:py-7 hover:bg-slate-50/60 transition"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="text-[12px] tabular-nums tracking-wider font-semibold mt-1.5 shrink-0"
          style={{ color: "#6B7A8D" }}>[{String(idx + 1).padStart(2, "0")}]</span>
        <span className="flex-1 font-heading font-semibold text-[18px] md:text-[20px] leading-snug tracking-tight"
          style={{ color: "#0F2A6E", letterSpacing: "-0.015em" }}>
          {q}
        </span>
        <CaretDown size={16} weight="bold"
          className={`mt-1.5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          style={{ color: "#1B6FE0" }} />
      </button>
      <div className={`grid transition-all duration-400 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-6 md:px-8 pb-7 pl-[3.5rem] md:pl-[5rem] text-[14.5px] md:text-[14.5px] text-[#1C2B3A] leading-[1.85] font-light whitespace-pre-line">
            {a}
          </div>
        </div>
      </div>
    </li>
  );
}

export default function Faq() {
  const { pg, pgList } = useSiteContent();
  const heading = pg("faq.heading", "Frequently asked questions");
  const sub = pg("faq.sub", "Quick answers to the questions we hear most often. Can't find what you need? Send us a note and we'll get back within 2 business hours.");
  const items = pgList("faq.items", [
    { q: "What does OpSysDigital actually deliver?", a: "We bring together engineering brands across infrastructure cabling, biometric attendance, building automation, energy management, AI and digital marketing — under one accountable team. End-to-end: strategy, deployment and after-sales." },
    { q: "Which regions do you operate in?", a: "MENA from our Dubai HQ, plus active operations in India, Bahrain JV, USA and a Zambia JV — with current expansion into Djibouti, Ethiopia, South Sudan and Somaliland." },
    { q: "Do you work with system integrators or only end clients?", a: "Both. We support 600+ system integrators, 50+ MEP contractors and 100+ direct enterprise clients including Etisalat, Du Telecom, Emaar, Masdar, Azizi and others." },
    { q: "What kind of warranty do your products carry?", a: "Cabling products (Ritchfield, B3) carry a 25-year performance warranty. Biometric devices (Time Watch / TrueFace) come with a 2-year manufacturer warranty." },
    { q: "How quickly can you respond to a brief?", a: "Within 2 business hours for inbound enquiries and same-day for existing accounts. Detailed proposals typically land within 3–5 working days, fully NDA-protected." },
  ]);

  // Left panel form state
  const [form, setForm] = useState({ name: "", designation: "", phone: "", email: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [submitting, setSubmitting] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (form.phone && !/^\+?[\d\s\-()]{6,20}$/.test(form.phone)) return toast.error("Phone format looks off");
    setSubmitting(true);
    try {
      const message = [
        form.designation && `Designation: ${form.designation}`,
        
        form.message && `Project: ${form.message}`,
      ].filter(Boolean).join("\n");
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: form.phone || "",
        message: message || "(no project description provided)",
      });
      toast.success("Got it — we'll respond within 2 hours.");
      setForm({ name: "", designation: "", phone: "", email: "", message: "" });
    } catch { toast.error("Could not send — please try again or email us directly."); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="faq" data-testid="faq-section" className="py-10 md:py-20 bg-white reveal" style={{ marginTop: "-10px" }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.1]"
            style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
            {heading}
          </h2>
          <p className="mt-4 text-[#6B7A8D] text-base md:text-lg leading-relaxed font-light max-w-2xl">
            {sub}
          </p>
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
          {/* Left panel — Didn't find what you were looking for? */}
          <form onSubmit={submit} data-testid="faq-form"
            className="relative p-7 md:p-8 rounded-3xl text-white overflow-hidden lg:sticky lg:top-24"
            style={{ background: "linear-gradient(170deg, #0F2A6E 0%, #1B6FE0 110%)" }}>
            <div className="absolute inset-0 grid-bg opacity-12" />
            <div className="relative">
              <h3 className="font-heading font-bold text-2xl tracking-tight leading-snug" style={{ letterSpacing: "-0.015em" }}>
                Didn't find what you were looking for?
              </h3>
              <p className="mt-2 text-[13.5px] text-slate-200/85 leading-relaxed font-light">
                We've got more answers waiting for you. Send us a quick note and we'll respond fast.
              </p>

              <div className="mt-6 space-y-3.5">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.12em] text-slate-200/75 font-semibold">Name</label>
                  <Input data-testid="faq-name" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)}
                    className="mt-1 bg-transparent border-0 border-b border-white/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.12em] text-slate-200/75 font-semibold">Designation</label>
                  <Input data-testid="faq-designation" placeholder="Your role" value={form.designation} onChange={(e) => set("designation", e.target.value)}
                    className="mt-1 bg-transparent border-0 border-b border-white/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.12em] text-slate-200/75 font-semibold">Contact number</label>
                    <Input data-testid="faq-phone" placeholder="+971…" value={form.phone}
                      onChange={(e) => set("phone", e.target.value.replace(/[^\d+\-\s()]/g, ""))}
                      className="mt-1 bg-transparent border-0 border-b border-white/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.12em] text-slate-200/75 font-semibold">Work email *</label>
                    <Input data-testid="faq-email" type="email" placeholder="you@company.com" value={form.email} onChange={(e) => set("email", e.target.value)} required
                      className="mt-1 bg-transparent border-0 border-b border-white/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.12em] text-slate-200/75 font-semibold">Describe your requirements</label>
                  <Textarea data-testid="faq-message" rows={4} placeholder="Project scope, brand preference, timelines and any specific requirements…" value={form.message} onChange={(e) => set("message", e.target.value)}
                    className="mt-1 bg-transparent border-0 border-b border-white/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40 resize-none" />
                </div>
              </div>

              <Button type="submit" disabled={submitting} data-testid="faq-submit"
                className="mt-6 w-full h-11 rounded-full bg-white hover:bg-blue-50 font-semibold"
                style={{ color: "#0F2A6E" }}>
                {submitting ? "Sending…" : "Submit"}
                <ArrowRight size={15} weight="bold" />
              </Button>
            </div>
          </form>

          {/* Right — FAQ accordion */}
          <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden" data-testid="faq-accordion">
            <ul className="divide-y divide-slate-100">
              {items.map((it, i) => (
                <FaqItem key={i} idx={i} q={it.q} a={it.a}
                  open={openIdx === i}
                  onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
